import React, { useContext, useState } from 'react';
import { CellContext, ColumnDefTemplate } from '@tanstack/react-table';
import { Box, Chip, ChipProps, Stack, Tooltip, Typography } from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import {
    FirestoreUserRole,
    Invitation,
    InvitationStatus,
    useDeleteEmailInviteMutation,
    useSendEmailInviteMutation,
} from 'generated/graphql';
import { useNotificationContext } from 'app/contexts/NotificationContext';
import UserContext from '../context/UsersContext';
import dayjs from 'dayjs';
import relativeTime from "dayjs/plugin/relativeTime";
import { ReplayOutlined } from "@mui/icons-material";
import Action from "Components/Dashboard/ReviewTable/components/Action";
import ConfirmationModal from "Components/Common/ConfirmationModal";
import { useAppSelector } from "hooks/index";
import { selectUserData } from "store/user/userSelector";
import { selectPartnerData, selectTeamsForTeamSelector } from "store/partner/partnerSelector";

dayjs.extend(relativeTime);

export const InvitationStatusCell: ColumnDefTemplate<
    CellContext<Invitation, InvitationStatus>
> = ({ row, getValue }) => {
    const { expiresAt } = row.original;
    const status = getValue();

    if (!expiresAt) return <Chip label="-" size="small" />;

    const now = dayjs();
    const expiration = dayjs.unix(expiresAt);
    const isExpired = expiration.isBefore(now);

    if (isExpired) {
        return (
            <Chip label="Expired" variant="outlined" color="info" size="small" />
        );
    }

    const hoursLeft = expiration.diff(now, "hour");

    const expireColor: ChipProps["color"] = hoursLeft <= 24 ? "error" : hoursLeft <= 48 ? "warning" : "success";

    switch (status) {
        case InvitationStatus.Accepted:
            return (
                <Chip label="Accepted" variant="outlined" color="success" size="small" />
            );

        case InvitationStatus.Pending: {
            const expiresLabel = `Expires in ${expiration.fromNow(true)}`;
            return (
                <Stack direction="row" spacing={0.5}>
                    <Chip label="Pending" variant="outlined" color={'success'} size="small" />
                    <Chip label={expiresLabel} variant="outlined" color={expireColor} size="small" />
                </Stack>
            );
        }
        case InvitationStatus.Canceled:
            return <Chip label="Canceled" size="small" />;

        default:
            return <Chip label="-" size="small" />;
    }
};

export const InvitationsTeamCell: ColumnDefTemplate<
    CellContext<Invitation, FirestoreUserRole>
> = ({ getValue }) => {
    const teamId = getValue();
    const teams = useAppSelector(selectTeamsForTeamSelector);
    return teams.find(t => t.key === teamId)?.name || 'Unknown team';
};

export const InvitationsRoleCell: ColumnDefTemplate<
    CellContext<Invitation, FirestoreUserRole>
> = ({ getValue }) => {
    const role = getValue();

    switch (role) {
        case FirestoreUserRole.User:
            return 'User';

        case FirestoreUserRole.OrganizationAdmin:
            return 'Organization Admin';

        default:
            return 'Unknown role';
    }
};

export const ActionsCell: ColumnDefTemplate<
    CellContext<Invitation, string>
> = ({ row }) => {
    const { email, id: rowUserId, expiresAt, teamId, organizationId, role } = row.original;

    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [showResendInviteModal, setShowResendInviteModal] = useState<boolean>(false);

    const { showNotification } = useNotificationContext();
    const { removeInvitation, updateInvitations } = useContext(UserContext);
    const { uid: userId } = useAppSelector(selectUserData);
    const { defaultTeamView: defaultTeamId } = useAppSelector(selectPartnerData);

    const [deleteInvite, { loading: deleting }] = useDeleteEmailInviteMutation({
        onError: () => {
            showNotification({
                message:
                    'There was an error while trying to delete this invite.',
                severity: 'error',
            });
        },
    });

    const [sendUserInvitations, { loading: resending }] = useSendEmailInviteMutation({
        onCompleted: () => {
            showNotification({
                message: `We've sent invitation to ${email}`,
                severity: 'success',
            });
        },
        onError: () => {
            showNotification({
                message: 'Unable to send email invitation',
                severity: 'error',
            });
        },
    });

    const handleDelete = async () => {
        try {
            await deleteInvite({
                variables: {
                    input: {
                        id: rowUserId,
                    },
                },
            });
            showNotification({
                message: 'The invite has been successfully deleted!',
                severity: 'success',
            });
            setShowDeleteModal(false);
            removeInvitation(rowUserId);
        } catch (error) {
            console.error(`Something went wrong when deleting invite. Error: ${error}`);
        }
    };

    const handleResend = async () => {
        try {
            await deleteInvite({
                variables: {
                    input: {
                        id: rowUserId,
                    },
                },
            });

            const {
                data: { sendEmailInvite: invitesSent },
            } = await sendUserInvitations({
                variables: {
                    input: {
                        instanceId: organizationId,
                        teamId: teamId || defaultTeamId,
                        inviterId: userId,
                        invitations: [{ email, role }],
                    },
                },
            });

            setShowResendInviteModal(false);
            updateInvitations(rowUserId, invitesSent);
        } catch (error) {
            console.error(`Something went wrong when resending invite. Error: ${error}`);
        }
    };

    const handleOnCancel = () => {
        setShowResendInviteModal(false);
        setShowDeleteModal(false)
    }

    return (
        <Box display="flex" alignItems="center">
            <Action
                title={'Archive invite'}
                onClick={() => setShowDeleteModal(true)}
                icon={<ArchiveOutlinedIcon />}
            />

            {dayjs().unix() > expiresAt && (
                <Action
                    title={'Resend invite'}
                    onClick={() => setShowResendInviteModal(true)}
                    icon={<ReplayOutlined />}
                />
            )}

            <ConfirmationModal
                open={showDeleteModal}
                onClose={handleOnCancel}
                title={'Delete invitation?'}
                content={
                    <Stack spacing={1}>
                        <Typography>
                            You are about to delete the invite for  <strong>{email}</strong>.
                        </Typography>
                        <Typography>This action will disable the current invitation link and cannot be undone.</Typography>
                    </Stack>
                }
                footer={{
                    primaryButton: {
                        label: 'Delete',
                        onClick: handleDelete,
                        disabled: deleting,
                        loading: deleting
                    },
                    secondaryButton: {
                        label: 'Cancel',
                        onClick: handleOnCancel,
                    }
                }}
            />

            <ConfirmationModal
                open={showResendInviteModal}
                onClose={handleOnCancel}
                title={'Resend invitation?'}
                content={
                    <Stack spacing={1}>
                        <Typography>
                            You are about to resend an invitation to <strong>{email}</strong>.
                        </Typography>
                        <Typography>A new email will be sent, and the old invite link will be disabled.</Typography>
                    </Stack>
                }
                footer={{
                    primaryButton: {
                        label: 'Resend',
                        onClick: handleResend,
                        disabled: resending,
                        loading: resending
                    },
                    secondaryButton: {
                        label: 'Cancel',
                        onClick: handleOnCancel,
                    }
                }}
            />
        </Box>
    );
};
